import type { SolverConfig, SolverPresetId } from '../types/solver'
import { applyActionSet } from '../lib/tree'

export const OOP_SAMPLE_RANGE = 'JJ-22,AQs-A2s,AQo-AJo,KQs-K8s,KQo,QJs-Q9s,JTs-J9s,T9s-76s,65s-54s'
export const IP_SAMPLE_RANGE = 'AA-22,AKs-A2s,AKo-ATo,KQs-K6s,KQo-KJo,QJs-Q8s,QJo,JTs-J8s,T9s-65s,54s'

export const presetMeta: Record<SolverPresetId, { name: string; short: string; stability: string }> = {
  'river-test': { name: 'River Test', short: 'リバー限定・最速', stability: '安定対象' },
  'turn-test': { name: 'Turn Test', short: 'ターン→リバー', stability: '試験対象' },
  'flop-lite': { name: 'Flop Lite', short: '2サイズ・レイズなし', stability: '安定対象' },
  'flop-standard': { name: 'Flop Standard', short: '複数サイズ・レイズあり', stability: '実験的' },
}

export function createDefaultConfig(preset: SolverPresetId = 'river-test'): SolverConfig {
  const now = new Date().toISOString()
  const config: SolverConfig = {
    id: crypto.randomUUID(),
    version: 2,
    createdAt: now,
    preset,
    game: { variant: 'NLHE', players: 2 },
    positions: { oop: 'BB', ip: 'BTN' },
    board: { flop: ['As', '7d', '2c'] },
    potBb: 5.5,
    effectiveStackBb: 97.5,
    ranges: {
      oop: { notation: OOP_SAMPLE_RANGE, label: 'BB call — サンプル' },
      ip: { notation: IP_SAMPLE_RANGE, label: 'BTN open — サンプル' },
    },
    tree: {
      flopBetPercent: 33,
      turnBetPercent: 50,
      riverBetPercent: 50,
      betSizes: { flop: [33, 75], turn: [50, 75], river: [50, 100] },
      raises: true,
      allIn: true,
    },
    accuracy: { targetExploitabilityPercent: 1, maxIterations: 80 },
    memoryMode: 'compact',
    memoryLimitMb: 320,
  }
  return applyPreset(config, preset)
}

export function applyPreset(config: SolverConfig, preset: SolverPresetId): SolverConfig {
  const next = structuredClone(config)
  next.id = crypto.randomUUID()
  next.createdAt = new Date().toISOString()
  next.preset = preset
  delete next.board.turn
  delete next.board.river
  const strategic = applyActionSet(next, 'strategic')
  next.tree = strategic.tree
  next.version = strategic.version
  next.memoryMode = 'compact'

  if (preset === 'river-test') {
    next.board.turn = 'Kh'
    next.board.river = '3s'
    next.accuracy = { targetExploitabilityPercent: 0.5, maxIterations: 100 }
  } else if (preset === 'turn-test') {
    next.board.turn = 'Kh'
    next.accuracy = { targetExploitabilityPercent: 0.8, maxIterations: 80 }
  } else if (preset === 'flop-lite') {
    next.tree.raises = false
    next.accuracy = { targetExploitabilityPercent: 1.5, maxIterations: 60 }
  } else {
    next.accuracy = { targetExploitabilityPercent: 1, maxIterations: 100 }
  }
  return next
}

export function createBenchmarkConfig(): SolverConfig {
  const config = createDefaultConfig('river-test')
  config.ranges.oop = { notation: 'AA,KK,QQ,AQs,AKo', label: 'Benchmark OOP' }
  config.ranges.ip = { notation: 'JJ,TT,AJs,KQs,AQo', label: 'Benchmark IP' }
  config.accuracy = { targetExploitabilityPercent: 5, maxIterations: 12 }
  config.memoryLimitMb = 128
  return config
}
